import { useEffect, useRef, useState } from "react";
import Navbar from "./Navbar";
import { useIsMobile } from "@/hooks/use-mobile";

const HeroSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scrollPadding, setScrollPadding] = useState(0);
  const cursorRef = useRef<HTMLDivElement>(null);
  const targetPos = useRef({ x: -400, y: -400 });
  const currentPos = useRef({ x: -400, y: -400 });
  const rafId = useRef<number | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [hideScrollHint, setHideScrollHint] = useState(false);
  const scrollHintShown = useRef(false);
  const hasScrolled = useRef(false);

  const isMobile = useIsMobile();
  // On mobile: render in final state immediately. On desktop: stage the intro.
  const [navIn, setNavIn] = useState(false);
  const [titleIn, setTitleIn] = useState(false);
  const [subtitleIn, setSubtitleIn] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Cinematic intro sequence (desktop only). Waits for video to be visible. Skipped if user scrolls.
  useEffect(() => {
    if (isMobile) {
      setNavIn(true);
      setTitleIn(true);
      setSubtitleIn(true);
      return;
    }
    if (!videoReady) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    timeouts.push(setTimeout(() => setNavIn(true), 50));
    timeouts.push(setTimeout(() => setTitleIn(true), 950));
    timeouts.push(setTimeout(() => setSubtitleIn(true), 1650));

    const skipOnScroll = () => {
      timeouts.forEach(clearTimeout);
      setNavIn(true);
      setTitleIn(true);
      setSubtitleIn(true);
      window.removeEventListener("scroll", skipOnScroll);
    };
    window.addEventListener("scroll", skipOnScroll, { passive: true });

    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener("scroll", skipOnScroll);
    };
  }, [isMobile, videoReady]);

  // Detect when the hero video has actually painted its first frame
  useEffect(() => {
    if (isMobile) return;
    const v = videoRef.current;
    if (!v) return;
    // If already playing with frames available
    if (v.readyState >= 3 && !v.paused) {
      setVideoReady(true);
      return;
    }
    const markReady = () => setVideoReady(true);
    v.addEventListener("playing", markReady);
    v.addEventListener("loadeddata", () => {
      if (v.readyState >= 3) markReady();
    });
    return () => {
      v.removeEventListener("playing", markReady);
    };
  }, [isMobile]);

  // Show scroll hint after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasScrolled.current) {
        setShowScrollHint(true);
        scrollHintShown.current = true;
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Hide scroll hint on scroll (once)
  useEffect(() => {
    const handleFirstScroll = () => {
      if (scrollHintShown.current && !hasScrolled.current) {
        hasScrolled.current = true;
        setHideScrollHint(true);
        setTimeout(() => setShowScrollHint(false), 600);
      }
    };
    window.addEventListener("scroll", handleFirstScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleFirstScroll);
  }, []);

  // Scroll-based padding & border-radius
  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const progress = Math.min(scrolled / (viewportH * 0.4), 1);
      setScrollPadding(progress * 40);
    };
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(compute);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cursor follower — direct DOM updates with rAF + lerp for buttery smoothness
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const onMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      targetPos.current.x = e.clientX - rect.left;
      targetPos.current.y = e.clientY - rect.top;
    };
    const onEnter = (e: MouseEvent) => {
      // Snap on enter so it doesn't dart in from old coordinates
      const rect = section.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      targetPos.current = { x, y };
      currentPos.current = { x, y };
      if (cursorRef.current) {
        cursorRef.current.style.opacity = "1";
      }
    };
    const onLeave = () => {
      if (cursorRef.current) {
        cursorRef.current.style.opacity = "0";
      }
    };

    const tick = () => {
      const dx = targetPos.current.x - currentPos.current.x;
      const dy = targetPos.current.y - currentPos.current.y;
      currentPos.current.x += dx * 0.18;
      currentPos.current.y += dy * 0.18;
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${currentPos.current.x - 160}px, ${currentPos.current.y - 160}px, 0)`;
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);

    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseenter", onEnter);
    section.addEventListener("mouseleave", onLeave);
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      section.removeEventListener("mousemove", onMove);
      section.removeEventListener("mouseenter", onEnter);
      section.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const borderRadius = scrollPadding > 0 ? Math.min(scrollPadding * 0.7, 28) : 0;

  return (
    <section
      id="hero-section"
      ref={sectionRef}
      className="relative w-full h-screen bg-white"
      style={{
        padding: `${scrollPadding}px`,
        transition: "padding 0.05s linear",
      }}
    >
      <div
        ref={innerRef}
        className="relative w-full h-full overflow-hidden"
        style={{
          borderRadius: `${borderRadius}px`,
          transition: "border-radius 0.05s linear",
        }}
      >
        {/* Background video */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          disableRemotePlayback
          preload="auto"
          {...({ fetchpriority: "high" } as any)}
          src="/videos/hero-bg.webm"
        />

        <Navbar visible={navIn} />

        {/* Cursor lens flare effect */}
        <div
          ref={cursorRef}
          className="pointer-events-none absolute top-0 left-0 z-20"
          style={{
            width: 320,
            height: 320,
            opacity: 0,
            background: `
              radial-gradient(circle, hsla(45,100%,95%,0.35) 0%, transparent 20%),
              radial-gradient(circle, hsla(205,100%,80%,0.4) 0%, hsla(205,90%,70%,0.15) 30%, transparent 60%),
              radial-gradient(ellipse at 40% 40%, hsla(200,100%,90%,0.2) 0%, transparent 50%),
              radial-gradient(ellipse at 60% 60%, hsla(210,80%,85%,0.15) 0%, transparent 50%)
            `,
            borderRadius: "50%",
            filter: "blur(2px)",
            mixBlendMode: "screen",
            transition: "opacity 250ms ease",
            willChange: "transform, opacity",
          }}
        />

        {/* Hero text - vertically centered, left aligned */}
        <div className="relative z-10 flex flex-col justify-center h-full px-8 md:px-16 lg:px-24">
          <h1
            className="font-poppins text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.1] mb-4"
            style={{
              opacity: titleIn ? 1 : 0,
              transform: titleIn ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 700ms ease-out, transform 700ms ease-out",
              willChange: "opacity, transform",
            }}
          >
            We Create
            <br />
            <span className="relative inline-block">
              <span className="font-cursive text-primary">Glorious</span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient id="underline-grad" x1="0" y1="0" x2="200" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="white" />
                    <stop offset="50%" stopColor="hsl(205, 90%, 75%)" />
                    <stop offset="100%" stopColor="hsl(205, 90%, 62%)" />
                  </linearGradient>
                </defs>
                <path
                  d="M2 8 C30 2, 60 12, 100 6 C140 0, 170 10, 198 4"
                  stroke="url(#underline-grad)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>{" "}
            Experiences
          </h1>
          <p
            className="font-nunito text-lg md:text-xl text-white/70 max-w-xl font-semibold leading-relaxed"
            style={{
              opacity: subtitleIn ? 1 : 0,
              transform: subtitleIn ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 700ms ease-out, transform 700ms ease-out",
              willChange: "opacity, transform",
            }}
          >
            A full-service digital agency crafting premium brands, immersive websites, and digital products that move people.
          </p>
        </div>

        {/* Scroll indicator */}
        {showScrollHint && (
          <div
            className={`absolute bottom-8 right-8 z-20 flex flex-col items-center gap-2 transition-opacity duration-500 ${
              hideScrollHint ? "opacity-0" : "opacity-100"
            }`}
            style={{
              animation: hideScrollHint ? "none" : "fadeInUp 0.8s ease-out",
            }}
          >
            <span className="font-nunito text-sm font-semibold text-white/80 tracking-widest uppercase">
              Scroll
            </span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="animate-bounce text-white/80"
            >
              <path
                d="M10 4 L10 14 M5 10 L10 15 L15 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
